import { format, isAfter, isBefore, isValid, parse } from 'date-fns'
import FocusTrap from 'focus-trap-react'
import { ChangeEventHandler, useRef, useState } from 'react'
import { DayPicker, DayPickerProps, SelectSingleEventHandler } from 'react-day-picker'

import 'react-day-picker/dist/style.css'

import { CalendarDaysIcon } from '@heroicons/react/20/solid'
import { usePopper } from 'react-popper'

import cn from '@/utilities/cn'

export default function DateDayPicker({
  onSelectDay,
  ...props
}: DayPickerProps & { onSelectDay: (date: Date) => void }) {
  const initialSelectedDate = () =>
    props.selected &&
    props.fromDate &&
    isAfter(props.selected as Date, props.fromDate) &&
    props.toDate &&
    isBefore(props.selected as Date, props.toDate)
      ? props.selected
      : new Date()
  const [selected, setSelected] = useState<Date | undefined>(initialSelectedDate() as Date)
  const [inputValue, setInputValue] = useState<string>(selected ? format(selected, 'y-MM-dd') : '')
  const [isPopperOpen, setIsPopperOpen] = useState(false)
  const [invalidInput, setInvalidInput] = useState(false)
  const popperRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null)

  const popper = usePopper(popperRef.current, popperElement, {
    placement: 'bottom-start'
  })

  const closePopper = () => {
    setIsPopperOpen(false)
    buttonRef?.current?.focus()
  }

  const handleInputChange: ChangeEventHandler<HTMLInputElement> = e => {
    setInputValue(e.currentTarget.value || e.target.value)
    const date = parse(e.currentTarget.value || e.target.value, 'y-MM-dd', new Date())
    if (isValid(date)) {
      if ((props.fromDate && isBefore(date, props.fromDate)) || (props.toDate && isAfter(date, props.toDate))) {
        setInvalidInput(true)
      } else {
        setInvalidInput(false)
        setSelected(date)
        onSelectDay(date)
      }
    }
  }

  const handleButtonClick = () => {
    setIsPopperOpen(true)
  }

  const handleDaySelect: SelectSingleEventHandler = date => {
    setInvalidInput(false)
    setSelected(date)
    if (date) {
      setInputValue(format(date, 'y-MM-dd'))
      onSelectDay(date)
      closePopper()
    } else {
      setInputValue('')
    }
  }

  return (
    <div>
      <div className="my-2 flex rounded-md shadow-sm" ref={popperRef}>
        <input
          className={cn(
            invalidInput
              ? 'text-red-900 ring-1 ring-inset ring-red-300 placeholder:text-red-300 focus:ring-2 focus:ring-inset focus:ring-red-500'
              : 'text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600',
            'block w-full rounded-none rounded-l-md border-0 py-1.5 pl-5 sm:text-sm sm:leading-6'
          )}
          name="day"
          size={12}
          type="text"
          placeholder={format(new Date(), 'y-MM-dd')}
          value={inputValue}
          onChange={handleInputChange}
        />
        <button
          ref={buttonRef}
          className="relative -ml-px inline-flex items-center gap-x-1.5 rounded-r-md px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          type="button"
          aria-label="Pick a date"
          onClick={handleButtonClick}
        >
          <CalendarDaysIcon className="h-6 w-6" />
        </button>
      </div>
      {isPopperOpen && (
        <FocusTrap
          active
          focusTrapOptions={{
            initialFocus: false,
            allowOutsideClick: true,
            clickOutsideDeactivates: true,
            onDeactivate: closePopper,
            fallbackFocus: buttonRef.current || undefined
          }}
        >
          <div
            tabIndex={-1}
            style={popper.styles.popper}
            className="dialog-sheet box rdp-day_selected z-30 border-0 border-r-4 bg-white shadow hover:z-30 hover:bg-white [&_.my-selected]:bg-blue-600 [&_.my-selected]:text-white [&_.my-selected:hover]:bg-blue-600"
            {...popper.attributes.popper}
            ref={setPopperElement}
            role="dialog"
            aria-label="DayPicker calendar"
          >
            <DayPicker
              modifiersClassNames={{
                selected: 'my-selected',
                today: 'my-today'
              }}
              initialFocus={isPopperOpen}
              mode="single"
              defaultMonth={selected}
              selected={selected}
              // @ts-expect-error
              onSelect={handleDaySelect}
              {...props}
            />
          </div>
        </FocusTrap>
      )}
    </div>
  )
}
